import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  ListGroup,
  Card,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { ref, get, push } from "firebase/database";
import { database } from "../firebaseConfig";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth"; // Import Firebase Auth
import { useNavigate } from "react-router-dom";

const StudentPage = () => {
  const [sharedData, setSharedData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answerFile, setAnswerFile] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [email, setEmail] = useState(""); // Assuming you want to take email input for user
  const auth = getAuth(); // Initialize Firebase Auth
  const [showUpdates, setShowUpdates] = useState(true);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hasUploadedAnswer, setHasUploadedAnswer] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUser(auth.currentUser);
    }
  }, [auth]);

  useEffect(() => {
    const fetchSharedData = async () => {
      try {
        const snapshot = await get(ref(database, "shared_data/"));
        const data = snapshot.val();
        const dataList = [];

        for (let id in data) {
          dataList.push({ id, ...data[id] });
        }

        setSharedData(dataList);
      } catch (error) {
        console.error("Error fetching data from Firebase:", error);
      }
    };

    fetchSharedData();

    // Listen for user authentication state
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setEmail(user.email); // Set authenticated user's email
      } else {
        setEmail(""); // Clear email if no user is signed in
      }
    });
  }, [auth]); // Dependency on auth state

  const handleShowDetails = (data) => {
    setSelectedData(data);
    // Check if the user has already uploaded an answer for this selected data
    const answerRef = ref(database, `shared_data/${data.id}/answers/`);
    get(answerRef).then((snapshot) => {
      const answers = snapshot.val() || {};
      const userAnswerExists = Object.values(answers).some(
        (answer) => answer.email === email
      );
      setHasUploadedAnswer(userAnswerExists); // Set the state to true if answer exists
    });
  };

  const handleShowQuestionModal = () => {
    setShowQuestionModal(true);
  };

  const handleCloseQuestionModal = () => {
    setShowQuestionModal(false);
  };

  const handleShowAnswerModal = () => {
    setShowAnswerModal(true);
  };

  const handleCloseAnswerModal = () => {
    setShowAnswerModal(false);
  };

  const handleAnswerFileChange = (e) => {
    const file = e.target.files[0];
    setAnswerFile(file);
  };

  const handleFeedbackChange = (e) => {
    setFeedback(e.target.value);
  };

  const uploadAnswerFile = async () => {
    if (!answerFile || !email || !selectedData) {
      setUploadError("Please ensure all required information is provided.");
      return;
    }

    // Define a list of GitHub repositories to try
    const repositories = [
      "A1",
      "A2",
      "A3",
      "A4",
      "A5",
      "A6",
      "A8",
      "A9",
      "A10",
    ];

    try {
      // Check if the user already uploaded an answer for this shared_data ID
      const answerRef = ref(
        database,
        `shared_data/${selectedData.id}/answers/`
      );
      const snapshot = await get(answerRef);
      const answers = snapshot.val() || {};

      const userAnswerExists = Object.values(answers).some(
        (answer) => answer.email === email
      );

      if (userAnswerExists) {
        setUploadError(
          "You have already uploaded an answer for this shared data."
        );
        return;
      }

      // Start uploading process
      setUploading(true);
      setUploadError(null); // Clear any existing errors
      setUploadProgress(0);

      // Generate a custom file name using the user's name and timestamp
      const timestamp = new Date()
        .toISOString()
        .split(".")[0]
        .replace(/[-T:]/g, "");
      const userName = email.split("@")[0]; // Adjust logic to use the full name if available
      const customFileName = `${userName}_${timestamp}_${answerFile.name}`;

      // Convert the file to base64
      const fileData = await toBase64(answerFile);

      // Try uploading to the repositories sequentially
      let uploadSuccess = false;
      let fileLink = "";

      for (const repo of repositories) {
        try {
          const response = await fetch(
            `https://api.github.com/repos/pragyeshias/${repo}/contents/answers/${customFileName}`,
            {
              method: "PUT",
              headers: {
                Authorization: `token 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`,
                Accept: "application/vnd.github.v3+json",
              },
              body: JSON.stringify({
                message: `Upload Answer Paper: ${customFileName}`,
                content: fileData,
              }),
            }
          );

          if (!response.ok) {
            console.warn(`Failed to upload to repository: ${repo}`);
            continue;
          }

          // Simulate upload progress
          for (let progress = 0; progress <= 100; progress += 20) {
            setUploadProgress(progress);
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          // If successful, construct the file link and exit the loop
          fileLink = `https://raw.githubusercontent.com/pragyeshias/${repo}/main/answers/${customFileName}`;
          uploadSuccess = true;
          break;
        } catch (error) {
          console.error(`Error uploading to repository ${repo}:`, error);
        }
      }

      if (!uploadSuccess) {
        throw new Error("Failed to upload to all repositories.");
      }

      // Save the file link and associated data in Firebase
      await push(ref(database, `shared_data/${selectedData.id}/answers/`), {
        fileLink,
        email,
        uploadedAt: new Date().toISOString(),
      });

      alert("Answer file uploaded successfully!");
      setUploadSuccess(true);
      setUploading(false);
      setAnswerFile(null);
      setShowAnswerModal(false);
    } catch (err) {
      console.error("Error uploading answer file:", err);
      setUploadError("Failed to upload answer file. Please try again later.");
      setUploading(false);
    }
  };

  const uploadFeedback = async () => {
    if (!feedback || !email || !selectedData) {
      console.error("Missing feedback, email, or shared_data ID");
      return;
    }

    try {
      const feedbackData = {
        feedback,
        email,
        submittedAt: new Date().toISOString(),
      };

      const feedbackRef = ref(
        database,
        `shared_data/${selectedData.id}/feedback/`
      );
      await push(feedbackRef, feedbackData);

      console.log("Feedback submitted successfully!");
      setFeedback("");
    } catch (err) {
      console.error("Error submitting feedback:", err);
    }
  };

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };
  return (
    <div className="student-page ">
      <Container fluid style={{ marginTop: "8px", padding: "0 15px" }}>
        <h1 className="mt-4 text-center  highlight">Student Dashboard</h1>
        <p className="text-center sub-text">
          Click on any item to view more details.
        </p>
        {user && (
          <a
            href="/student"
            className="position-fixed bottom-0 start-0 mb-2 ms-2 d-flex align-items-center p-2 text-decoration-none"
            style={{
              zIndex: 1050,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "30px",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            }}
          >
            <img
              src={user.photoURL}
              alt={user.email}
              className="rounded-circle me-2"
              style={{
                height: "50px",
                width: "50px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                border: "2px solid #007BFF",
              }}
            />
            <span
              style={{ color: "#000", fontWeight: "600", fontSize: "1rem" }}
            >
              {user.displayName || user.email}
            </span>
          </a>
        )}

        {/* Responsive toggle button */}
        <div className="d-block d-md-none text-center mb-3">
          <Button
            variant="info"
            onClick={() => setShowUpdates(!showUpdates)}
            className="w-100 py-2 rounded-3 shadow-sm"
            style={{ transition: "0.3s" }}
          >
            <i className="bi bi-file-earmark-text me-2"></i> Show Available
            Updates
          </Button>
        </div>

        <Row className="g-4">
          {/* Left Section: List of Data */}
          <Col
            xs={12}
            md={4}
            className={`${showUpdates ? "" : "d-none"} d-md-block`}
          >
            <Card
              className="shadow-lg border-0 rounded-3 sticky-top"
              style={{
                top: "80px",
                backgroundColor: "#000",
                color: "#fff",
              }}
            >
              <Card.Body>
                <h5 className="text-white">
                  <i className="bi bi-file-earmark-text me-2"></i> Available
                  Updates
                </h5>

                {/* Search Bar */}
                <Form.Control
                  type="text"
                  placeholder="Search Question Paper..."
                  className="mb-3"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                  style={{
                    backgroundColor: "#fff",
                    color: "#000",
                    borderColor: "#444",
                  }}
                />

                <ListGroup>
                  {sharedData
                    .filter(
                      (data) => data.title.toLowerCase().includes(searchTerm) // Filter by title
                    )
                    .map((data) => (
                      <ListGroup.Item
                        key={data.id}
                        action
                        onClick={() => handleShowDetails(data)}
                        className={`mb-3 p-3 rounded-3 shadow-sm hover-shadow ${
                          selectedData?.id === data.id
                            ? "bg-primary text-white"
                            : "bg-black text-light"
                        }`}
                        style={{
                          cursor: "pointer",
                          transition: "0.3s",
                        }}
                      >
                        <i className="bi bi-file-earmark-text me-2"></i>
                        {data.title}
                      </ListGroup.Item>
                    ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Section: Data Details */}
          <Col xs={12} md={8}>
            {selectedData ? (
              <Card
                className="shadow-lg border-0 rounded-3"
                style={{
                  backgroundColor: "#000", // Black background
                  color: "#fff", // White text for better contrast
                }}
              >
                <Card.Body>
                  <h5 className="text-white">
                    <i className="bi bi-file-earmark-richtext me-2"></i>
                    {selectedData.title}
                  </h5>

                  {/* Video Link Section */}
                  {selectedData.answers &&
                    Object.values(selectedData.answers)
                      .filter((answer) => answer.email === email) // Filter answers by user's email
                      .map((answer, index) => (
                        <div key={index} className="mb-3">
                          <h6>
                            <strong>Video/Link:</strong>{" "}
                            <i className="bi bi-link-45deg"></i>
                          </h6>
                          {answer?.video_link?.link ? (
                            answer.video_link.link.endsWith(".pdf") ||
                            answer.video_link.link.endsWith(".doc") ||
                            answer.video_link.link.endsWith(".docx") ? (
                              <iframe
                                src={answer.video_link.link}
                                width="100%"
                                height="400px"
                                frameBorder="0"
                                title="Document"
                                className="rounded-3 shadow-sm"
                              ></iframe>
                            ) : answer.video_link.link.includes(
                                "youtube.com"
                              ) ||
                              answer.video_link.link.includes("youtu.be") ? (
                              <iframe
                                src={
                                  answer.video_link.link.includes("watch?v=")
                                    ? answer.video_link.link.replace(
                                        "watch?v=",
                                        "embed/"
                                      )
                                    : answer.video_link.link.replace(
                                        "youtu.be/",
                                        "www.youtube.com/embed/"
                                      )
                                }
                                width="100%"
                                height="400px"
                                frameBorder="0"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title="YouTube Video"
                                className="rounded-3 shadow-sm"
                              ></iframe>
                            ) : (
                              <iframe
                                width="100%"
                                height="400px"
                                src={answer.video_link.link}
                                frameBorder="0"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title="Video"
                                className="rounded-3 shadow-sm"
                              ></iframe>
                            )
                          ) : (
                            <p>No video or link available.</p>
                          )}
                        </div>
                      ))}

                  {/* Description Section */}
                  <div className="mb-3">
                    <h6>
                      <strong>Description:</strong>{" "}
                      <i className="bi bi-info-circle-fill"></i>
                    </h6>
                    <p>{selectedData.description}</p>
                  </div>

                  {/* Question Paper Link Button */}
                  {/* Buttons Row */}
                  <div className="row">
                    {/* View Question Paper Button */}
                    <div className="col-md-6 mb-3">
                      <Button
                        onClick={handleShowQuestionModal}
                        className="w-100 py-2 rounded-3 text-white"
                        style={{
                          backgroundColor: "#1A73E8", // Google Blue color
                          borderColor: "#1A73E8", // Matching border color
                          transition: "0.3s", // Smooth transition on hover
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Soft shadow for depth
                          fontWeight: "500", // Medium font weight for a modern feel
                        }}
                      >
                        <i
                          className="bi bi-file-earmark-pdf me-2"
                          style={{ fontSize: "1.2rem" }}
                        ></i>
                        View Question Paper
                      </Button>
                    </div>

                    {/* Upload Answer Paper Button */}
                    <div className="col-md-6 mb-3">
                      <Button
                        onClick={handleShowAnswerModal}
                        className="w-100 py-2 rounded-3 text-white"
                        style={{
                          backgroundColor: hasUploadedAnswer
                            ? "#28A745"
                            : "#FF5722", // Green if uploaded, Orange otherwise
                          borderColor: hasUploadedAnswer
                            ? "#28A745"
                            : "#FF5722", // Matching border color
                          transition: "0.3s", // Smooth transition on hover
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Soft shadow for depth
                          fontWeight: "500", // Medium font weight for a modern feel
                        }}
                      >
                        <i
                          className="bi bi-cloud-upload me-2"
                          style={{ fontSize: "1.2rem" }}
                        ></i>
                        {hasUploadedAnswer
                          ? "Answer Uploaded"
                          : "Upload Answer Paper"}
                      </Button>
                    </div>
                  </div>

                  {/* Evaluated Answers Section - Filtered by User Email */}
                  <div className="mb-3">
                    <h6 className="text-white">
                      <strong>Evaluated Answers:</strong>{" "}
                      <i className="bi bi-journal-check"></i>
                    </h6>
                    <ListGroup>
                      {selectedData.answers &&
                        Object.values(selectedData.answers)
                          .filter((answer) => answer.email === email) // Filter answers by user's email
                          .map((answer, index) => (
                            <ListGroup.Item
                              key={index}
                              className="mb-4 p-4 rounded-3 shadow-lg bg-white hover-shadow"
                              style={{
                                borderLeft: "5px solid #036e63", // Accent color (green) on the left side for a stylish touch
                                transition: "all 0.3s ease", // Smooth transition for hover effect
                              }}
                            >
                              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
                                <h5 className="text-dark fw-bold d-flex align-items-center">
                                  <i className="bi bi-person-circle me-2"></i>{" "}
                                  {answer.email}
                                </h5>
                                <small className="text-muted mt-2 mt-md-0">
                                  <i className="bi bi-calendar-check me-2"></i>
                                  Uploaded at:{" "}
                                  {new Date(answer.uploadedAt).toLocaleString()}
                                </small>
                              </div>

                              <div>
                                {/* Evaluated Answer Section */}
                                <div className="mb-3">
                                  <h6 className="text-primary d-flex align-items-center">
                                    <i className="bi bi-file-earmark-lock me-2"></i>
                                    <span className="fw-semibold">
                                      Evaluated Answer:
                                    </span>
                                  </h6>
                                  {answer.evaluated_answer &&
                                  answer.evaluated_answer.fileLink ? (
                                    <a
                                      href={answer.evaluated_answer.fileLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-outline-primary btn-sm px-4 py-2"
                                      style={{ textDecoration: "none" }}
                                    >
                                      View Evaluated Answer
                                      <span className="fw-bold ms-2">
                                        {" "}
                                        (Evaluated By:{" "}
                                        {answer.evaluated_answer.evaluator})
                                      </span>
                                    </a>
                                  ) : (
                                    <span className="text-danger">
                                      Evaluated Answer Report not available
                                    </span>
                                  )}
                                </div>

                                {/* Details Report Section */}
                                <div className="mb-3">
                                  <h6 className="text-info d-flex align-items-center">
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    <span className="fw-semibold">
                                      Details Report:
                                    </span>
                                  </h6>
                                  {answer.details_report &&
                                  answer.details_report.fileLink ? (
                                    <a
                                      href={answer.details_report.fileLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-outline-info btn-sm px-4 py-2"
                                      style={{ textDecoration: "none" }}
                                    >
                                      View Details Report
                                      <span className="fw-bold ms-2">
                                        {" "}
                                        (Evaluated By:{" "}
                                        {answer.evaluated_answer.evaluator})
                                      </span>
                                    </a>
                                  ) : (
                                    <span className="text-warning">
                                      Details Report not available
                                    </span>
                                  )}
                                </div>
                              </div>
                            </ListGroup.Item>
                          ))}
                    </ListGroup>
                  </div>

                  {/* Feedback Section */}
                  <div className="mb-3">
                    <h6>
                      <strong>Give Feedback:</strong>{" "}
                      <i className="bi bi-pencil-square"></i>
                    </h6>
                    <Form>
                      <Form.Group controlId="formFeedback">
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Provide your feedback here..."
                          value={feedback}
                          onChange={handleFeedbackChange}
                          className="rounded-3"
                          style={{
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                            transition: "0.3s",
                          }}
                        />
                      </Form.Group>
                      <Button
                        variant="success"
                        onClick={uploadFeedback}
                        className="mt-2 w-100 py-2 rounded-3"
                      >
                        <i className="bi bi-check-circle me-2"></i> Submit
                        Feedback
                      </Button>
                    </Form>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <Card className="shadow-sm">
                <Card.Body>
                  <p>Select an item from the left to view its details.</p>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      {/* Modal for Question Paper */}
      <Modal
        show={showQuestionModal}
        onHide={handleCloseQuestionModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-pdf me-2"></i> Question Paper
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <h6>
              <strong>Question Paper/Link:</strong>{" "}
              <i className="bi bi-link-45deg"></i>
            </h6>
            {selectedData && selectedData.questionPaperLink ? (
              (() => {
                const fileLink = selectedData.questionPaperLink;

                // Detect file type based on extension
                if (
                  fileLink.endsWith(".pdf") ||
                  fileLink.endsWith(".doc") ||
                  fileLink.endsWith(".docx")
                ) {
                  // Render documents
                  return (
                    <iframe
                      src={fileLink}
                      width="100%"
                      height="600px"
                      frameBorder="0"
                      title="Document"
                      className="rounded-3 shadow-sm"
                      allowFullScreen
                    ></iframe>
                  );
                } else if (
                  fileLink.endsWith(".jpg") ||
                  fileLink.endsWith(".jpeg") ||
                  fileLink.endsWith(".png") ||
                  fileLink.endsWith(".gif")
                ) {
                  // Render images
                  return (
                    <img
                      src={fileLink}
                      alt="Question Paper"
                      className="img-fluid rounded-3 shadow-sm"
                      style={{
                        maxHeight: "600px",
                        width: "100%",
                        objectFit: "contain",
                      }}
                    />
                  );
                } else if (
                  fileLink.endsWith(".mp4") ||
                  fileLink.endsWith(".webm") ||
                  fileLink.endsWith(".ogg")
                ) {
                  // Render videos
                  return (
                    <video
                      controls
                      className="rounded-3 shadow-sm w-100"
                      style={{ maxHeight: "600px" }}
                    >
                      <source
                        src={fileLink}
                        type={`video/${fileLink.split(".").pop()}`}
                      />
                      Your browser does not support the video tag.
                    </video>
                  );
                } else if (
                  fileLink.includes("youtube.com") ||
                  fileLink.includes("youtu.be")
                ) {
                  // Render YouTube links
                  return (
                    <iframe
                      src={
                        fileLink.includes("watch?v=")
                          ? fileLink.replace("watch?v=", "embed/")
                          : fileLink.replace(
                              "youtu.be/",
                              "www.youtube.com/embed/"
                            )
                      }
                      width="100%"
                      height="600px"
                      frameBorder="0"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="YouTube Video"
                      className="rounded-3 shadow-sm"
                    ></iframe>
                  );
                } else {
                  // Default fallback for unsupported files
                  return (
                    <div className="text-center">
                      <p className="text-warning">
                        Unable to display this file type. Please{" "}
                        <a
                          href={fileLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                        >
                          download the file
                        </a>{" "}
                        to view it.
                      </p>
                    </div>
                  );
                }
              })()
            ) : (
              <p>No question paper or link available.</p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseQuestionModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Logout Button */}
      <Button
        variant="danger"
        onClick={handleLogout}
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 10,
          display: "none",
        }}
      >
        <i className="bi bi-box-arrow-right"></i> Log Out
      </Button>

      {/* Modal for Answer Paper Upload */}
      <Modal
        show={showAnswerModal}
        onHide={handleCloseAnswerModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-cloud-upload me-2"></i> Upload Answer Paper
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* File Input */}
            <Form.Group controlId="formFile" className="mb-4">
              <Form.Label className="fw-bold d-flex align-items-center">
                <i className="bi bi-file-earmark-text-fill me-2 text-secondary fs-4"></i>
                Select Answer File
              </Form.Label>
              <div
                className="d-flex align-items-center border rounded-3 p-3"
                style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}
              >
                <i className="bi bi-upload text-primary fs-3 me-3"></i>
                <span className={answerFile ? "text-dark" : "text-muted"}>
                  {answerFile ? answerFile.name : "No file selected"}
                      
                </span>
              </div>
              <input
                type="file"
                onChange={handleAnswerFileChange}
                className="d-none" // Hide the default file input
                id="fileUploadInput"
              />
              <Button
                variant="outline-primary"
                className="mt-2"
                onClick={() =>
                  document.getElementById("fileUploadInput").click()
                }
              >
                Choose File
              </Button>
            </Form.Group>

            {/* Email Input */}
            <Form.Group controlId="formEmail" className="mb-4">
              <Form.Label className="fw-bold">
                <i className="bi bi-envelope-fill me-2 text-info"></i>Your Email
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={email}
                readOnly
                className="rounded-3"
                style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}
              />
            </Form.Group>

            {/* Progress Bar */}
            {uploading && (
              <div className="mt-4">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-primary fw-bold">Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="progress rounded-3" style={{ height: "10px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${uploadProgress}%`,
                      background: "linear-gradient(90deg, #28a745, #85e085)",
                    }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {uploadSuccess && (
              <div className="alert alert-success mt-4 text-center rounded-3">
                <i className="bi bi-check-circle-fill me-2"></i>
                Your file has been successfully uploaded!
              </div>
            )}

            {/* Error Message */}
            {uploadError && (
              <div className="alert alert-danger mt-4 text-center rounded-3">
                <i className="bi bi-x-circle-fill me-2"></i>
                {uploadError}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseAnswerModal}
            disabled={uploading}
          >
            Close
          </Button>
          <Button
            variant="success"
            onClick={uploadAnswerFile}
            disabled={uploading || uploadSuccess}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentPage;
